// @givmo/app-admin — the backend user-row bootstrap for the provider-first OTP path.
//
// The backend middleware only LOADS users by `idp_id` (no provisioning outside local), and the
// admin flows historically never needed to create the row early: company registration creates it,
// and the OTP always ran AFTER registration (the ordering the OTP screen's header documents —
// "avoids the pre-register 403 on /users/me/email_verification"). The provider-first verification
// path (microsoft.com users verify BEFORE registering, 2026-08-18) breaks that ordering, so the
// OTP send now bootstraps the row first.
//
// DELIBERATELY an app-local copy of the same dance in `apps/charity/src/auth/ensure-backend-user.ts`
// and `apps/donate/app/lib/p2p-client.client.ts` (`ensureBackendUser`) — per-app auth transport is
// this repo's established pattern (see agent_docs/auth.md, "There is no single sign-in path").
// `GET /users/me` → a 403 means "no row" (the backend answers 403, not 404) → `POST /users`
// (identity from the ID token; email rides along) → tolerate a 409 create-race only if the
// identity then resolves. Per-uid memo; a FAILED attempt clears its slot so the next call retries.

import { sdk, isGivmoError } from '@givmo/api-client';

const attempts = new Map<string, Promise<void>>();

function hasStatus(error: unknown, status: number): boolean {
  return isGivmoError(error) && error.status === status;
}

async function provision(email: string | null): Promise<void> {
  const existing = await sdk.usersGetMe();
  if (existing.data !== undefined) return;
  if (!hasStatus(existing.error, 403)) {
    throw existing.error ?? new Error('Your account could not be confirmed.');
  }

  const created = await sdk.usersCreateUser({ body: email ? { email } : {} });
  if (created.data !== undefined) return;

  if (hasStatus(created.error, 409)) {
    const confirmed = await sdk.usersGetMe();
    if (confirmed.data !== undefined) return;
  }

  throw created.error ?? new Error('Your account could not be created.');
}

/** Ensure the signed-in Firebase user has a backend `users` row. Idempotent per uid. */
export function ensureBackendUser(uid: string, email: string | null): Promise<void> {
  const existing = attempts.get(uid);
  if (existing !== undefined) return existing;
  const attempt = provision(email).catch((thrown: unknown) => {
    attempts.delete(uid);
    throw thrown;
  });
  attempts.set(uid, attempt);
  return attempt;
}

/** Test seam: forget every cached attempt (suites share module state). */
export function resetEnsureBackendUser(): void {
  attempts.clear();
}
